import { Fragment, useEffect, useState, useRef } from 'react';
import StyleAuxiliary from '../../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import TextField from '../../MDC/TextField/TextField';
import { Button } from '@upmc/ims.component-library';
import MaterialIcon from '../../MDC/Icon/Icon';
import { Headline6 } from '../../MDC/Typography/Typography';
import PropTypes from 'prop-types';
import classes from './FileUpload.module.scss';

const FileUpload = props => {
	const [sIsFileSelected, setIsFileSelected] = useState(false);
	const [sMessage, setMessage] = useState();

	/**
	 * Effect on 'props.resetSelection' change. Resets file selection.
	 */
	useEffect(() => {
		if (props.resetSelection) {
			refInput.current.value = null;
			setMessage(null);
		}
	}, [props.resetSelection]);
	const refInput = useRef();

	/**
	 * Event handler for file selection. Parses the file for header and body content and calls parent handler, ensuring file of expected type.
	 * File content is split and filtered against regular expression defined in props.
	 * An object with the following properties is created and passed to parent component: "fileName" (name of file),
	 *  "fileHeaders" (array of header columns in first row of file), "fileContent" (array of remaining rows of file)
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types|Common MIME types}
	 * @param {Event} e event raised when value of "Input" element of type "file" changes. Possesses a "FileList" object of selected file.
	 * @returns void
	 */
	const onClickHandler = e => {
		const files = e.target.files;
		const file = {
			fileName: '',
			fileHeaders: [],
			fileContent: [],
		};

		let isFileSelected = true;
		let message;

		if (files && files.length > 0) {
			file.fileName = files[0].name;
			message = file.fileName;
			if (
				props.fileMIMETypes &&
				!props.fileMIMETypes.split(',').includes(files[0].type)
			) {
				isFileSelected = false;
				message = 'Incorrect file type.';
			}
		} else {
			isFileSelected = false;
			message = 'No file selected.';
		}

		setIsFileSelected(isFileSelected);
		setMessage(message);
		if (!isFileSelected) {
			return;
		}

		var reader = new FileReader();
		reader.onloadend = function (event) {
			const domParser = new DOMParser();
			const result = domParser.parseFromString(
				event.target.result,
				'text/html'
			).documentElement.textContent;
			if (result && result.length > 0) {
				const rawContent = result
					.trim()
					.split(props.parseRegex)
					.filter(content => !props.parseRegex.test(content));
				let rowHeader = rawContent
					.splice(0, 1)[0]
					.trim()
					.split(/,[^\w]*/g);
				file.fileHeaders = rowHeader;
				file.fileContent = rawContent;

				props.onClickHandler(file);
			} else {
				setIsFileSelected(false);
				setMessage('Empty file selected.');
			}
		};
		reader.readAsText(files[0]);
	};

	return (
		<StyleAuxiliary classes={classes['container']}>
			<input
				accept={props.fileTypes ? props.fileTypes : '.txt,.csv'}
				id='inputFileUpload'
				type='file'
				onClick={() => (refInput.current.value = null)}
				onChange={e => onClickHandler(e)}
				className={classes['input--file']}
				ref={refInput}
			/>
			<Button
				sx={{width: '16rem'}}
				disabled={props.disabled}
				id='file-upload__button--upload'
				variant='outlined'
				onClick={() => refInput.current.click()}
				startIcon={<MaterialIcon icon='note_add' />}
			>
				Upload from File
			</Button>
			{sMessage ? (
				<div
					className={classes['input--file__result__container']}
					title={sMessage}
					aria-label={sMessage}
				>
					{sIsFileSelected ? (
						<TextField
							disabled
							outlined
							leadingIcon={
								<MaterialIcon icon='insert_drive_file' />
							}
							label={sMessage}
							inputProps={{
								disabled: true,
							}}
						/>
					) : (
						<Fragment>
							<Headline6
								className={
									classes['input--file__label--invalid']
								}
							>
								{sMessage}
							</Headline6>
						</Fragment>
					)}
				</div>
			) : null}
		</StyleAuxiliary>
	);
};

FileUpload.propTypes = {
	disabled: PropTypes.bool,
	fileTypes: PropTypes.string,
	fileMIMETypes: PropTypes.string,
	onClickHandler: PropTypes.func.isRequired,
	parseRegex: PropTypes.object.isRequired,
	resetSelection: PropTypes.bool,
};

export default FileUpload;
